import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Settings, Users, Calendar, BarChart3, ArrowRight, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useEvents } from '@/hooks/useEvents';
import { useProfile } from '@/hooks/useProfile';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useFeedWidgets, useUpdateFeedWidgets, FeedWidgetConfig } from '@/hooks/useFeedWidgets';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

function useNewMembers() {
  return useQuery({
    queryKey: ['feed-new-members'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, company, bio')
        .eq('membership_status', 'approved')
        .eq('onboarding_completed', true)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    },
  });
}

function useMemberSpotlight() {
  return useQuery({
    queryKey: ['feed-member-spotlight'],
    queryFn: async () => {
      // Get all approved members
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, company, bio')
        .eq('membership_status', 'approved')
        .eq('onboarding_completed', true);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Use today's date as seed for deterministic daily rotation
      const today = new Date();
      const daySeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

      // Simple hash-based shuffle seeded by day
      const shuffled = [...data].sort((a, b) => {
        const hashA = simpleHash(a.user_id + daySeed);
        const hashB = simpleHash(b.user_id + daySeed);
        return hashA - hashB;
      });

      return shuffled.slice(0, 3);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Simple deterministic hash for seeded shuffle */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash;
}

function useCommunityStats() {
  return useQuery({
    queryKey: ['feed-community-stats'],
    queryFn: async () => {
      const [membersRes, cohortsRes, eventsRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('membership_status', 'approved'),
        supabase
          .from('cohorts')
          .select('id', { count: 'exact', head: true }),
        supabase
          .from('events')
          .select('id', { count: 'exact', head: true }),
      ]);

      return {
        members: membersRes.count ?? 0,
        cohorts: cohortsRes.count ?? 0,
        events: eventsRes.count ?? 0,
      };
    },
  });
}

function NewMembersWidget() {
  const { data: members, isLoading } = useNewMembers();

  if (isLoading || !members?.length) return null;

  return (
    <Card>
      <CardHeader className="pb-3 pt-4 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Welcome New Members!
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="space-y-3">
          {members.map((member) => (
            <Link
              key={member.user_id}
              to={`/members/${member.user_id}`}
              className="flex items-center gap-2.5 group"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <SignedAvatarImage src={member.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                  {member.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                  {member.full_name}
                </p>
                {member.company && (
                  <p className="text-xs text-muted-foreground truncate">
                    {member.company}
                  </p>
                )}
                {member.bio && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {member.bio}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/directory"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors mt-3 pt-3 border-t"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

function MemberSpotlightWidget() {
  const { data: members, isLoading } = useMemberSpotlight();

  if (isLoading || !members?.length) return null;

  return (
    <Card>
      <CardHeader className="pb-3 pt-4 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          Member Spotlight
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="space-y-3">
          {members.map((member) => (
            <Link
              key={member.user_id}
              to={`/members/${member.user_id}`}
              className="flex items-center gap-2.5 group"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <SignedAvatarImage src={member.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                  {member.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                  {member.full_name}
                </p>
                {member.company && (
                  <p className="text-xs text-muted-foreground truncate">
                    {member.company}
                  </p>
                )}
                {member.bio && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {member.bio}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/directory"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors mt-3 pt-3 border-t"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

function UpcomingEventsWidget() {
  const { data: allEvents, isLoading } = useEvents();
  const { data: profile } = useProfile();

  if (isLoading || !allEvents?.length) return null;

  // Filter to events matching user's cohort or global (digital) events
  const userCohort = profile?.city_cohort;
  const relevantEvents = allEvents.filter((event) =>
    event.cohort_tag === 'global' ||
    event.cohort_tag === 'all' ||
    (userCohort && event.cohort_tag.toLowerCase() === userCohort.toLowerCase())
  );

  const upcoming = relevantEvents.slice(0, 3);

  if (!upcoming.length) return null;

  return (
    <Card>
      <CardHeader className="pb-3 pt-4 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="space-y-3">
          {upcoming.map((event) => (
            <Link
              key={event.id}
              to="/events"
              className="block group"
            >
              <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                {event.title}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {new Date(event.event_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
                <span>&middot;</span>
                <span>{event.attendees.length} attending</span>
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/events"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors mt-3 pt-3 border-t"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

function CommunityPulseWidget() {
  const { data: stats, isLoading } = useCommunityStats();

  if (isLoading || !stats) return null;

  return (
    <Card>
      <CardHeader className="pb-3 pt-4 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          Community Pulse
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="flex items-center justify-between text-center">
          <div>
            <p className="text-lg font-semibold">{stats.members}</p>
            <p className="text-xs text-muted-foreground">Members</p>
          </div>
          <div>
            <p className="text-lg font-semibold">{stats.cohorts}</p>
            <p className="text-xs text-muted-foreground">Cohorts</p>
          </div>
          <div>
            <p className="text-lg font-semibold">{stats.events}</p>
            <p className="text-xs text-muted-foreground">Events</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminWidgetSettings({
  config,
  onUpdate,
}: {
  config: FeedWidgetConfig;
  onUpdate: (config: FeedWidgetConfig) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
          <Settings className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52" align="end">
        <p className="text-xs font-medium mb-3">Sidebar Widgets</p>
        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={config.new_members}
              onCheckedChange={(checked) =>
                onUpdate({ ...config, new_members: checked === true })
              }
            />
            New Members
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={config.member_spotlight}
              onCheckedChange={(checked) =>
                onUpdate({ ...config, member_spotlight: checked === true })
              }
            />
            Member Spotlight
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={config.upcoming_events}
              onCheckedChange={(checked) =>
                onUpdate({ ...config, upcoming_events: checked === true })
              }
            />
            Upcoming Events
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={config.community_pulse}
              onCheckedChange={(checked) =>
                onUpdate({ ...config, community_pulse: checked === true })
              }
            />
            Community Pulse
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function FeedSidebar() {
  const { data: config } = useFeedWidgets();
  const updateWidgets = useUpdateFeedWidgets();
  const { isAdmin } = useIsAdmin();

  const widgetConfig = config ?? {
    new_members: true,
    member_spotlight: true,
    upcoming_events: true,
    community_pulse: true,
  };

  const hasAnyWidget =
    widgetConfig.new_members ||
    widgetConfig.member_spotlight ||
    widgetConfig.upcoming_events ||
    widgetConfig.community_pulse;

  if (!hasAnyWidget && !isAdmin) return null;

  return (
    <aside className="hidden xl:block sticky top-6 space-y-4 self-start">
      {isAdmin && (
        <div className="flex justify-end">
          <AdminWidgetSettings
            config={widgetConfig}
            onUpdate={(c) => updateWidgets.mutate(c)}
          />
        </div>
      )}
      {widgetConfig.new_members && <NewMembersWidget />}
      {widgetConfig.member_spotlight && <MemberSpotlightWidget />}
      {widgetConfig.upcoming_events && <UpcomingEventsWidget />}
      {widgetConfig.community_pulse && <CommunityPulseWidget />}
    </aside>
  );
}
